import { Form } from "@inertiajs/react"
import { XCircle } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { transitionsMissionCollabPath } from "@/routes"
import type { BrandMissionCollabRow } from "@/types/missions"

import { ReturnToFields } from "./collab-transition-return"

/**
 * OKS-483 — Brand-side `Decline reopen` confirm dialog. Sends a
 * `reopen_requested` collab back to terminal `expired` and notifies the
 * creator. The creator may request again (no v1 cap).
 *
 * Mirrors `reject-application-confirm-dialog.tsx` shape — single Inertia
 * `<Form>` POST with hidden `event=decline_reopen`, outline-destructive
 * trigger. Rendered ONLY on a `reopen_requested` row gated by
 * `can_decline_reopen` (UX Spec §C).
 */
interface DeclineReopenConfirmDialogProps {
  row: BrandMissionCollabRow
  missionId: number
}

export function DeclineReopenConfirmDialog({
  row,
  missionId,
}: DeclineReopenConfirmDialogProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const ariaTitle = row.creator.handle
    ? `@${row.creator.handle}`
    : row.creator.name

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        render={
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="text-destructive"
            aria-label={t("collabs.decline_reopen.aria", { title: ariaTitle })}
            data-testid="brand-mission-collab-decline-reopen-trigger"
          >
            <XCircle aria-hidden="true" className="mr-1 size-4" />
            {t("collabs.decline_reopen.cta")}
          </Button>
        }
      />
      <DialogContent
        data-testid="brand-mission-collab-decline-reopen-dialog"
        className="sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle>{t("collabs.decline_reopen.title")}</DialogTitle>
          <DialogDescription>
            {t("collabs.decline_reopen.body", { creator: row.creator.name })}
          </DialogDescription>
        </DialogHeader>
        <Form
          action={transitionsMissionCollabPath(missionId, row.id)}
          method="post"
          options={{ preserveScroll: true }}
          onSuccess={() => setOpen(false)}
        >
          {({ processing }) => (
            <>
              <input type="hidden" name="event" value="decline_reopen" />
              {/* OKS-1038 — return_to/collabs_shown on the crew member page only. */}
              <ReturnToFields />
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setOpen(false)}
                  data-testid="brand-mission-collab-decline-reopen-cancel"
                >
                  {t("collabs.decline_reopen.cancel")}
                </Button>
                <Button
                  type="submit"
                  variant="destructive"
                  disabled={processing}
                  autoFocus
                  data-testid="brand-mission-collab-decline-reopen-confirm"
                >
                  {t("collabs.decline_reopen.confirm")}
                </Button>
              </DialogFooter>
            </>
          )}
        </Form>
      </DialogContent>
    </Dialog>
  )
}
